"use client";

import { CarrierLoadOfferPortal } from "@/components/carrier/CarrierLoadOfferPortal";
import { CarrierOnboardingPortal } from "@/components/carrier/CarrierOnboardingPortal";
import { AppShell } from "@/components/console/app-shell";
import { ConsoleDashboardClient } from "@/components/console/ConsoleDashboardClient";
import { OperationsAgendaClient } from "@/components/console/OperationsAgendaClient";
import { InsightsClient } from "@/components/insights/InsightsClient";
import { ReviewerInbox } from "@/components/inbox/ReviewerInbox";
import { LoadDetailClient } from "@/components/loads/LoadDetailClient";
import { CustomerPerformanceReportView } from "@/components/scorecards/CustomerPerformanceReportView";
import { EntityScorecardView } from "@/components/scorecards/EntityScorecardView";
import { AutonomySettings } from "@/components/settings/AutonomySettings";
import { TrackingLinkView } from "@/components/tracking/TrackingLinkView";
import { PodUploadLinkView } from "@/components/uploads/PodUploadLinkView";
import { getConsoleSnapshot } from "@/lib/demo-data";
import { resolveTenantDefaultPath } from "@/core/auth/current-tenant";
import type { TenantMembershipSummary } from "@/core/auth/current-tenant";
import {
  AgenticTmsPortal,
  HaulbaseAgentPortal,
  HeadlessTenantPortal,
  ProductScopeGate,
} from "./ProductPortalViews";
import { OnboardingGate } from "./HaulbaseOnboardingFlow";
import { TenantStateGate } from "./TenantContextProvider";

export function TenantDefaultRoute() {
  return (
    <TenantStateGate>
      {(tenant) => {
        return <OnboardingGate tenant={tenant}>{renderDefaultTenantPortal(tenant)}</OnboardingGate>;
      }}
    </TenantStateGate>
  );
}

export function TenantAgentRoute() {
  return (
    <ProductScopeGate product="haulbase_agent" label="Haulbase Agent">
      {(tenant) => (
        <OnboardingGate tenant={tenant}>
          <HaulbaseAgentPortal tenant={tenant} />
        </OnboardingGate>
      )}
    </ProductScopeGate>
  );
}

export function TenantAgenticTmsRoute() {
  return (
    <ProductScopeGate product="full_agentic_tms" label="Haulbase Agentic TMS">
      {(tenant) => (
        <OnboardingGate tenant={tenant}>
          <AgenticTmsPortal tenant={tenant} />
        </OnboardingGate>
      )}
    </ProductScopeGate>
  );
}

function renderDefaultTenantPortal(tenant: TenantMembershipSummary) {
  const defaultPath = resolveTenantDefaultPath(tenant);

  if (defaultPath === "/agentic-tms") {
    return <AgenticTmsPortal tenant={tenant} />;
  }

  if (defaultPath === "/agent") {
    return <HaulbaseAgentPortal tenant={tenant} />;
  }

  return <HeadlessTenantPortal tenant={tenant} />;
}

export function TenantConsoleRoute() {
  const snapshot = getConsoleSnapshot();
  return (
    <TenantStateGate>
      {(tenant) => (
        <AppShell commandLoads={snapshot.loads.map(({ load }) => ({ ...load, tenantId: tenant.tenantId }))}>
          <ConsoleDashboardClient tenantId={tenant.tenantId} fallback={snapshot} />
        </AppShell>
      )}
    </TenantStateGate>
  );
}

// P1.1 keeps the calm "Operations Agenda" home reachable at /agenda now that the
// Command Center is the default console.
export function TenantAgendaRoute() {
  const snapshot = getConsoleSnapshot();
  return (
    <TenantStateGate>
      {(tenant) => (
        <AppShell commandLoads={snapshot.loads.map(({ load }) => ({ ...load, tenantId: tenant.tenantId }))}>
          <OperationsAgendaClient tenantId={tenant.tenantId} />
        </AppShell>
      )}
    </TenantStateGate>
  );
}

export function TenantInboxRoute() {
  return <TenantStateGate>{(tenant) => <ReviewerInbox tenantId={tenant.tenantId} />}</TenantStateGate>;
}

export function TenantInsightsRoute() {
  return <TenantStateGate>{(tenant) => <InsightsClient tenantId={tenant.tenantId} />}</TenantStateGate>;
}

export function TenantLoadDetailRoute({ loadId }: { loadId: string }) {
  return <TenantStateGate>{(tenant) => <LoadDetailClient tenantId={tenant.tenantId} loadId={loadId} />}</TenantStateGate>;
}

export function TenantScorecardRoute({
  kind,
  entityId,
}: {
  kind: "customer" | "carrier";
  entityId: string;
}) {
  return (
    <TenantStateGate>
      {(tenant) => <EntityScorecardView tenantId={tenant.tenantId} kind={kind} entityId={entityId} />}
    </TenantStateGate>
  );
}

export function TenantAutonomyRoute() {
  return <TenantStateGate>{(tenant) => <AutonomySettings tenantId={tenant.tenantId} />}</TenantStateGate>;
}

export function TenantCarrierOnboardingRoute({ token }: { token?: string }) {
  return (
    <TenantStateGate>
      {(tenant) => <CarrierOnboardingPortal tenantId={tenant.tenantId} token={token ?? ""} />}
    </TenantStateGate>
  );
}

export function TenantCarrierOfferRoute({ token }: { token?: string }) {
  return (
    <TenantStateGate>
      {(tenant) => <CarrierLoadOfferPortal tenantId={tenant.tenantId} token={token ?? ""} />}
    </TenantStateGate>
  );
}

export function TenantTrackingRoute({ token }: { token: string }) {
  return <TenantStateGate>{(tenant) => <TrackingLinkView tenantId={tenant.tenantId} token={token} />}</TenantStateGate>;
}

export function TenantCustomerReportRoute({ token }: { token: string }) {
  return (
    <TenantStateGate>
      {(tenant) => <CustomerPerformanceReportView tenantId={tenant.tenantId} token={token} />}
    </TenantStateGate>
  );
}

export function TenantPodUploadRoute({ token }: { token: string }) {
  return <TenantStateGate>{(tenant) => <PodUploadLinkView tenantId={tenant.tenantId} token={token} />}</TenantStateGate>;
}
